import { HdrAuto, HeatPump, PanTool } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { Appliance, ApplianceConfigHeatPump, ApplianceSubtype } from './Appliance'
import { RelayAppliance } from './RelayAppliance'
import { PortControl } from '../ports/PortControl'
import { HeatPumpPortControl } from '../ports/HeatPumpPortControl'
import { ID, Port, Strategy } from '../../types/models'
import { HeatPumpConnType, PortConfig, PortType } from '../../types/ports'
import { getWidget } from '../widgets'
import { HeatPumpStateWidget } from '../widgets/HeatPumpStateWidget'
import { Widget } from '../widgets/Widget'
import { Safety } from '../Safety'
import { ChipSelector } from '../../components/atoms/ChipSelector'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { Dropdown } from '../../components/atoms/Dropdown'
import { Text } from '../../components/atoms/Text'
import { StrategySliders } from '../../components/StrategySliders'
import { strategyPortConfig } from './strategy'
import {
  HEAT_PUMP_COMFORT, HEAT_PUMP_FLOW_MAX_PRESETS, HEAT_PUMP_FLOW_MAX_RANGE, HEAT_PUMP_OPTIMIZER_DEFAULTS,
  HeatPumpCalibration, heatPumpCalibration, heatPumpComfortBand, heatPumpComfortOffset
} from './heat-pump-defaults'

/**
 * "Air-to-water heat pump NEW": a heat pump on port 0x23 (Modbus, e.g. Midea
 * M-Thermal), planned by the firmware optimizer through its own
 * optimizer_control instance (port = 23/0, power levels 0..7 = electrical
 * input targets).
 *
 * Pilot, behind the HEAT_PUMP_APPLIANCE feature. Strategies: the heat pump's
 * own control (optimizer channel off, the machine runs on its own weather
 * curve) or automatic optimization. All defaults live in heat-pump-defaults.ts.
 *
 * User settings: "More saving / More heat" shifts the comfort setpoint (indoor
 * temperature, +-3 K), the time window sets the model's time constant, and an
 * optional indoor temperature sensor closes the loop. The heat demand model
 * (duty at two outdoor temperatures, maximum electrical input) is a per-house
 * calibration in an installer-only section.
 */
export class HeatPumpAppliance extends RelayAppliance<ApplianceConfigHeatPump> {

  get Icon() {
    return HeatPump
  }

  getDefaultPortType(): PortType {
    return 'HEAT_PUMP'
  }

  isSuitable(ctrl: PortControl<Port, PortConfig>, subtype: ApplianceSubtype): boolean {
    return ctrl.port.type === 'HEAT_PUMP'
  }

  /**
   * Only heat pump ports of devices that still have an optimizer control free for them.
   */
  filterSuitablePorts(ports: PortControl<Port, PortConfig>[]): PortControl<Port, PortConfig>[] {
    return ports.filter(ctrl => (ctrl as HeatPumpPortControl).optimizerControl() !== null)
  }

  hasTestPage() {
    return false
  }

  getHeatPumpControl(): HeatPumpPortControl {
    return this.getControl(0)
  }

  getDefaultConfig(): ApplianceConfigHeatPump {
    return {
      strategy: 'AUTO',
      plan_by_price: true,
      pv_priority: HEAT_PUMP_OPTIMIZER_DEFAULTS.pv_priority,
      saving_or_heat: 0,
      heat_preservation: HEAT_PUMP_OPTIMIZER_DEFAULTS.time_constant,
      use_temperature: false,
    }
  }

  getDefaultWidgets(): Widget[] {
    return [
      getWidget({
        name: '',
        type: 'HEAT_PUMP_STATE',
        appliances: [this],
        config: {}
      }, this.control, this.app) as HeatPumpStateWidget,
    ]
  }

  drawSliders(): React.ReactNode {
    const offset = heatPumpComfortOffset(this.getConfig('saving_or_heat'))
    const target = (HEAT_PUMP_COMFORT.minTemp + HEAT_PUMP_COMFORT.maxTemp) / 2 + offset

    return <Box>
      <StrategySliders
        name1="saving_or_heat"
        slider1={[this.app.t('More Saving'), this.app.t('More Heat')]}
        values1={[-100, 100]}
        value1={this.getConfig('saving_or_heat')}
        title1={this.app.t('Do you prefer?')}

        name2="heat_preservation"
        slider2={['4h', '24h']}
        values2={[4, 24]}
        value2={this.getConfig('heat_preservation')}
        title2={this.app.t('Time window of optimization?')}

        onChange={(k, v) => { this.setConfig(k, v); this.applyConfig() }}
      />
      <Box sx={{ mt: 1, fontSize: '90%' }} data-cy="text-Heat pump comfort target">
        {this.app.t('Indoor temperature target {target} °C ({offset} K).')
          .replace('{target}', target.toFixed(1))
          .replace('{offset}', `${offset > 0 ? '+' : ''}${offset.toFixed(1)}`)}
      </Box>
    </Box>
  }

  /**
   * Optional indoor temperature sensor (a Temperature appliance on this
   * device's 0x104 port): with it the plan starts from the measured indoor
   * temperature and the firmware learns the house's heat loss.
   */
  drawSensorSelector(appliances: Appliance[]): React.ReactNode {
    const useTemperature = !!this.getConfig('use_temperature')
    const portId = this.getConfig<ID | undefined>('temperature_port_id')
    const deviceId = this.getHeatPumpControl().device.id
    const sensors = appliances.filter(app =>
      app.type === 'TEMPERATURE' && app.controls.length > 0 &&
      app.controls[0].port.port_number !== '104/0' && app.controls[0].device.id === deviceId)
    const options: Record<string, string> = {}
    sensors.forEach(s => (options[s.controls[0].port.id + ''] = s.getName()))

    return <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CustomSwitch
          data-cy="switch-Use Temperature"
          disabled={sensors.length === 0 && !useTemperature}
          onClick={() => { this.setConfig('use_temperature', !useTemperature); this.applyConfig() }}
          checked={useTemperature}
        />
        <Box sx={{ ml: 2 }}><Trans>Use an indoor temperature sensor</Trans></Box>
      </Box>
      {useTemperature &&
        <Dropdown
          sx={{ ml: 2 }}
          oneLine
          label={this.app.t('Sensor to use')}
          items={options}
          selected={portId ? `${portId}` : null}
          onSelect={(v) => { this.setConfig('temperature_port_id', parseInt(v || '0') || null); this.applyConfig() }}
        />
      }
    </Box>
  }

  /**
   * Installer-only: the house's heat demand model. Fitted per house; the user
   * settings above do not touch it.
   */
  drawCalibration(): React.ReactNode {
    const cal = heatPumpCalibration(this.getConfig('calibration'))
    const stored = (this.getConfig<Partial<HeatPumpCalibration> | undefined>('calibration') || {}) as Record<string, unknown>
    const shown = (key: keyof HeatPumpCalibration) => stored[key] !== undefined ? stored[key] as string | number : cal[key]
    const set = (key: keyof HeatPumpCalibration, value: unknown) =>
      this.setConfig('calibration', { ...stored, [key]: value })
    const commit = (key: keyof HeatPumpCalibration, raw: string, integer = true) => {
      const num = integer ? parseInt(raw) : parseFloat(raw.replace(',', '.'))
      set(key, Number.isFinite(num) ? num : undefined)
      this.applyConfig()
    }
    const field = (key: keyof HeatPumpCalibration, label: string, unit: string, min: number, max: number) =>
      <Text
        data-cy={`text-Heat pump calibration ${key}`}
        label={label}
        size="small"
        type="number"
        unit={unit}
        min={min}
        max={max}
        value={shown(key)}
        onChange={(e) => set(key, e.target.value)}
        onBlur={(e) => commit(key, e.target.value)}
      />

    return <Box sx={{ mt: 3 }} data-cy="box-Heat pump calibration">
      <Typography><Trans>Heat demand model (installer)</Trans></Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 2, mt: 2 }}>
        {field('appliance_power', this.app.t('Maximum electrical input'), 'W', 500, 60000)}
        <Box/>
        {field('max_duty', this.app.t('Electrical duty in cold weather'), '%', 0, 100)}
        {field('max_duty_at', this.app.t('at outdoor temperature'), '°C', -40, 10)}
        {field('min_duty', this.app.t('Electrical duty in mild weather'), '%', 0, 100)}
        {field('min_duty_at', this.app.t('at outdoor temperature'), '°C', 0, 25)}
      </Box>
    </Box>
  }

  /**
   * The heat pump port settings: how the Enion reaches the machine and how far
   * the driver may raise the flow setpoint. Written to the 0x23 port conf.
   */
  drawHeatPumpSettings(): React.ReactNode {
    const hp = this.getHeatPumpControl()
    const [flowMin, flowMax] = HEAT_PUMP_FLOW_MAX_RANGE

    return <Box sx={{ mt: 3 }} data-cy="box-Heat pump settings">
      <Typography><Trans>Heat pump connection</Trans></Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 2, mt: 2 }}>
        <Dropdown
          label={this.app.t('RS485 port')}
          items={{
            HEATPUMP_CONN_RTU_RS485_0: 'RS485 4/0',
            HEATPUMP_CONN_RTU_RS485_1: 'RS485 4/1',
          }}
          selected={hp.getConfig('conn_type')}
          onSelect={(v) => hp.setConfig('conn_type', v as HeatPumpConnType)}
        />
        <Text
          data-cy="text-Modbus address"
          label={this.app.t('Modbus address')}
          size="small"
          type="number"
          integer
          positive
          min={1}
          max={64}
          value={hp.getConfig('slave_id') ?? ''}
          onChange={(e) => hp.setConfig('slave_id', e.target.value)}
          onBlur={(e) => hp.setConfig('slave_id', parseInt(e.target.value) || 1)}
        />
        <Text
          data-cy="text-Maximum flow temperature"
          label={this.app.t('Maximum flow temperature')}
          size="small"
          type="number"
          integer
          unit="°C"
          min={flowMin}
          max={flowMax}
          value={hp.getConfig('flow_setpoint_max') ?? ''}
          onChange={(e) => hp.setConfig('flow_setpoint_max', e.target.value)}
          onBlur={(e) => hp.setConfig('flow_setpoint_max', parseInt(e.target.value) || HEAT_PUMP_FLOW_MAX_PRESETS.UNDERFLOOR)}
        />
      </Box>
      <Box sx={{ mt: 1, fontSize: '85%' }}>
        {this.app.t('Underfloor heating {underfloor} °C, radiators {radiators} °C.')
          .replace('{underfloor}', `${HEAT_PUMP_FLOW_MAX_PRESETS.UNDERFLOOR}`)
          .replace('{radiators}', `${HEAT_PUMP_FLOW_MAX_PRESETS.RADIATORS}`)}
      </Box>
    </Box>
  }

  configTop(appliances: Appliance[]): React.ReactNode {
    const installerLevel = !!Safety.get('installer-level')
    const strategy = this.getConfig<Strategy | undefined>('strategy')
    const optimized = ['AUTO', 'PV_ONLY', 'AVOID_EXPENSIVE'].includes(strategy || '')

    const items: Record<string, string> = {
      MANUAL: this.app.t('HeatPump-Strategy-MANUAL'),
      AUTO: this.app.t('Strategy-AUTO'),
    }
    // A strategy set elsewhere (e.g. by hand on the port) stays visible.
    if (strategy && !(strategy in items)) {
      items[strategy] = this.app.t(`Strategy-${strategy}`)
    }

    const onSelectStrategy = (value: string) => {
      this.setConfig('strategy', value)
      const conf = strategyPortConfig(value as Strategy)
      const ctrl = this.getOptimizerControl()
      Object.keys(conf).forEach(k => ctrl.setConfig(k, conf[k as keyof typeof conf], false))
      this.applyConfig()
    }

    return <Box>
      <Trans>Strategy</Trans>
      <ChipSelector
        sx={{ mt: 1 }}
        items={items}
        icons={[PanTool, HdrAuto]}
        selected={strategy || null}
        onSelect={onSelectStrategy}
        onePerLine
      />
      {strategy === 'MANUAL' &&
        <Box sx={{ mt: 1, fontSize: '90%' }}><Trans>The heat pump runs on its own settings. Enion only shows its status.</Trans></Box>
      }
      {optimized && <Box>
        {this.drawSliders()}
        {this.drawSensorSelector(appliances)}
        {installerLevel && this.drawCalibration()}
      </Box>}
      {this.drawHeatPumpSettings()}
    </Box>
  }

  /**
   * optimizer_control conf derived from the appliance (spp_optimizer_control.h,
   * HEAT-PUMP CHANNELS). The strategy adds enabled / plan_by_price (optimizerConfig()).
   */
  convert(): Record<string, unknown> {
    const defaults = HEAT_PUMP_OPTIMIZER_DEFAULTS
    const cal = heatPumpCalibration(this.getConfig('calibration'))
    const [minTemp, maxTemp] = heatPumpComfortBand(this.getConfig('saving_or_heat'))
    const sensorPortId = this.getConfig<ID | undefined>('temperature_port_id')
    const sensorPort = this.getConfig('use_temperature') && sensorPortId
      ? this.control.findPortControl(sensorPortId)?.port.port_number
      : undefined

    return {
      port: this.getHeatPumpControl().port.port_number,
      cont_dest_kind: 'DEST_KIND_THIS_DEVICE',
      cont_dest: 0,
      use_clock_based_duty: defaults.use_clock_based_duty,
      time_constant: this.getConfig('heat_preservation') ?? defaults.time_constant,
      appliance_power: cal.appliance_power,
      min_duty: cal.min_duty,
      max_duty: cal.max_duty,
      min_duty_at: cal.min_duty_at,
      max_duty_at: cal.max_duty_at,
      cop_curve: [...defaults.cop_curve],
      good_sink: defaults.good_sink,
      rapid_switching_ok: defaults.rapid_switching_ok,
      fuse_priority: defaults.fuse_priority,
      pv_priority: this.getConfig('pv_priority') ?? defaults.pv_priority,
      min_temp: minTemp,
      max_temp: maxTemp,
      abs_min_temp: minTemp - HEAT_PUMP_COMFORT.absMargin,
      abs_max_temp: maxTemp + HEAT_PUMP_COMFORT.absMargin,
      use_temperature_meas: !!sensorPort,
      tempsens_dest_kind: 'DEST_KIND_THIS_DEVICE',
      tempsens_dest: 0,
      tempsens_port: sensorPort || '0/0',
    }
  }

  /**
   * Unlike relays, fuse protection is not copied from the PV priority: the heat
   * pump follows target changes in minutes, not seconds.
   */
  createHook(): void {
    if (this.getConfig('strategy') === 'MANUAL') {
      this.getOptimizerControl().setConfig('enabled', false)
    }
  }
}
